import React from 'react';
import styled from 'styled-components';
import { Text, InputNumber, Button } from 'components';
import { BeforeAfter } from './types';

type TimePickerProps = {
  time: BeforeAfter['time'];
  setTime: React.Dispatch<React.SetStateAction<BeforeAfter>>;
};

const TimePicker = ({
  time: { hour, minute, am },
  setTime,
}: TimePickerProps) => {
  const invertAmPm = () =>
    setTime(prev => ({ ...prev, time: { ...prev.time, am: !prev.time.am } }));

  const setHour = (_hour: number) =>
    setTime(prev => ({ ...prev, time: { ...prev.time, hour: _hour } }));
  const setMinute = (_minute: number) =>
    setTime(prev => ({ ...prev, time: { ...prev.time, minute: _minute } }));

  const onHoursStep = (val: number) => {
    const newHour = val % 12 || 12;
    if ((newHour === 11 && hour === 12) || (newHour === 12 && hour === 11))
      invertAmPm();
    setHour(newHour);
  };

  const onMinutesStep = (
    value: number,
    info: { offset: number; type: 'up' | 'down' },
  ) => {
    const isIncrement = info.type === 'up';
    const newMin = (minute + (isIncrement ? 15 : 45)) % 60;
    const increased = newMin > minute;
    if (increased !== isIncrement) {
      onHoursStep(hour + (increased ? 11 : 1));
    }
    setMinute(newMin);
  };

  return (
    <HourMinInputContainer>
      <LabelContainer>
        <Text>hour</Text>
        <Text>min</Text>
      </LabelContainer>
      <InputNumbersContainer>
        <StyledInputNumber
          key="hours"
          value={hour}
          onStep={onHoursStep}
          onInput={e => {
            const _hour = Number(e);
            if (_hour < 13 && _hour > 0) setHour(_hour);
          }}
          alwaysShowArrows
        />
        :
        <StyledInputNumber
          key="minutes"
          value={minute}
          formatter={value => (value === '0' ? '00' : `${value}`)}
          parser={value =>
            Number.isNaN(parseInt(value, 10)) ? value : parseInt(value, 10)
          }
          step={15}
          onStep={onMinutesStep}
          onInput={e => {
            const _minute = Number(e);
            if (
              _minute === 0 ||
              _minute === 15 ||
              _minute === 30 ||
              _minute === 45
            )
              setMinute(_minute);
          }}
          alwaysShowArrows
        />
        <AmPmButton onClick={invertAmPm}>{am ? 'AM' : 'PM'}</AmPmButton>
      </InputNumbersContainer>
    </HourMinInputContainer>
  );
};

const HourMinInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: auto;
`;
const InputNumbersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const LabelContainer = styled.div`
  display: flex;
  gap: 48px;
  margin-bottom: 4px;
`;
const StyledInputNumber = styled(InputNumber)`
  width: 56px;
  height: 32px;
  border: 1px solid #d9d9d9;
  box-sizing: border-box;
  border-radius: 2px;
  input {
    padding: 0 0 2px 6px;
  }
  & .ant-input-number {
    height: 30px;
  }
`;
const AmPmButton = styled(Button)`
  width: 44px;
  height: 32px;
  padding: unset;
  border-radius: 2px;
`;

export default TimePicker;
